import { Container} from '@mui/material'
import React, { useCallback, useContext, useEffect, useState } from "react"
import OperationFilter from "../../components/Filter/OperationFilter"
import { MultimodalDownloadOption } from "../../components/OverviewView/ImageInfoDownload"
import DataTable, { HeadCell, IDataTableId } from "../../components/Misc/DataTable/DataTable"
import { IOperationFilter, Modality, OperationsFieldOrderBy } from "../../Store/Operations/types"
import DatatableFooter from '../../components/Misc/DataTable/DatatableFooter'
import EnhancedTableToolbar from '../../components/Misc/DataTable/EnchancedTableToolbar'
import { useSnackbar } from 'notistack'
import { APIsContext } from '../../Store/APIs/context'
import { Operation } from 'neurotec-megamatcherid-management-client'
import fileDownload from 'js-file-download'
import { NavigationContext } from '../../Store/Navigation/context'
import { setLoading } from '../../Store/Navigation/reducer'
import OverviewDialog from '../../components/OverviewView/OverviewDialog'
import { FieldsOrder } from '../../Store/Subjects/types'
import ConfirmationButton from '../../components/Misc/Dialog/ConfirmationButton'
import DeleteIcon from '@mui/icons-material/Delete';

const faceHeadCells: HeadCell[] = [
    {
        id: 'OPERATION_DATE',
        align: 'left',
        disablePadding: false,
        label: 'Date',
        width: "19%"
    },
    {
        id: 'OPERATION_STATUS',
        align: 'left',
        disablePadding: false,
        label: 'Status',
        width: "12%"
    },
    {
        id: 'OPERATION_OPERATION_TYPE',
        align: 'left',
        disablePadding: false,
        label: 'Operation type',
        width: "12%"
    },
    {
        id: 'OPERATION_LIVENESS_MODE',
        align: 'left',
        disablePadding: false,
        label: 'Liveness mode',
        width: "11%"
    },
    {
        id: 'OPERATION_LIVENESS_SCORE',
        align: 'left',
        disablePadding: false,
        label: 'Liveness score',
        width: "11%"
    },
    {
        id: 'OPERATION_AGE',
        align: 'left',
        disablePadding: false,
        label: 'Estimated age',
        width: "13%"
    },
    {
        id: 'OPERATION_QUALITY',
        align: 'left',
        disablePadding: false,
        label: 'Quality',
        width: "17%"
    },
];

const voiceHeadCells: HeadCell[] = [
    {
        id: 'OPERATION_DATE',
        align: 'left',
        disablePadding: false,
        label: 'Date',
        width: "25%"
    },
    {
        id: 'OPERATION_STATUS',
        align: 'left',
        disablePadding: false,
        label: 'Status',
        width: "25%"
    },
    {
        id: 'OPERATION_OPERATION_TYPE',
        align: 'left',
        disablePadding: false,
        label: 'Operation type',
        width: "25%"
    },
    {
        id: 'OPERATION_QUALITY',
        align: 'left',
        disablePadding: false,
        label: 'Quality',
        width: "25%"
    },
];

const fingersHeadCells: HeadCell[] = [
    {
        id: 'OPERATION_DATE',
        align: 'left',
        disablePadding: false,
        label: 'Date',
        width: "25%"
    },
    {
        id: 'OPERATION_STATUS',
        align: 'left',
        disablePadding: false,
        label: 'Status',
        width: "25%"
    },
    {
        id: 'OPERATION_OPERATION_TYPE',
        align: 'left',
        disablePadding: false,
        label: 'Operation type',
        width: "25%"
    },
    {
        id: 'OPERATION_QUALITY',
        align: 'left',
        disablePadding: false,
        label: 'Quality',
        width: "25%"
    },
];

const Operations: React.FC = () => {

    const { enqueueSnackbar } = useSnackbar();
    const navigationDispatch = useContext(NavigationContext).dispatch
    const [operations, setOperations] = useState<Operation[]>([]);
    const [operationsFilter, setOperationsFilter] = useState<IOperationFilter>({fieldsOrder: FieldsOrder.DESC, operationsFieldToOrderBy: OperationsFieldOrderBy.OPERATION_DATE, modalities: [Modality.FACE_MODALITY]});
    const [operationsCount, setOperationsCount] = useState<number | undefined>(undefined);
    const [showOperation, setShowOperation] = useState(false)
    const [selectedOperation, setSelectedOperation] = useState<Operation>();
    const [page, setPage] = useState(1)
    const [perPage, setPerPage] = useState(20)
    const [selected, setSelected] = useState<IDataTableId[]>([]);
    const { state } = useContext(APIsContext)

    const fetchOperation = useCallback(async () => {
        try {
            navigationDispatch(setLoading(true))
            state.OperationsApi.getLog({page: page, size: perPage, ...operationsFilter}).then(res => {
                setOperations(res.data)
                navigationDispatch(setLoading(false))
            }).catch(e => {
                console.log(e)
                enqueueSnackbar(e.message, {variant: "error"})
                navigationDispatch(setLoading(false))
            })

            state.OperationsApi.getLogCount(operationsFilter).then(res => {
                setOperationsCount(res.data)
            }).catch(e => {
                setOperationsCount(0)
                enqueueSnackbar(e.message, {variant: "error"})
            })

        } catch (error: any) {
            console.log(error)
            enqueueSnackbar(error.message, {variant: "error"})
        }
    }, [page, perPage, state.OperationsApi, operationsFilter, enqueueSnackbar, navigationDispatch])

    useEffect(() => {
        fetchOperation();
    }, [state.OperationsApi, operationsFilter, page, perPage, fetchOperation])

    const getDataTableType = (): "operations-face" | "operations-voice" | "operations-fingers" => {
        const modality = operationsFilter?.modalities?.[0] ?? Modality.FACE_MODALITY
        switch (modality){
            case Modality.FACE_MODALITY:
                return "operations-face"
            case Modality.VOICE_MODALITY:
                return "operations-voice"
            case Modality.FINGERS_MODALITY:
                return "operations-fingers"
            default:
                return "operations-face"
        }
    }

    const getHeadCells = (): HeadCell[] => {
        const modality = operationsFilter?.modalities?.[0] ?? Modality.FACE_MODALITY
        switch (modality){
            case Modality.FACE_MODALITY:
                return faceHeadCells
            case Modality.VOICE_MODALITY:
                return voiceHeadCells
            case Modality.FINGERS_MODALITY:
                return fingersHeadCells
            default:
                return faceHeadCells
        }
    }

    const handlePageChange = (event: React.ChangeEvent<unknown> | undefined, newPage: number) => {
        setPage(newPage)
    }

    const handleFilterChange = (filter: IOperationFilter) => {
        setPage(1)
        setOperationsCount(undefined)
        setOperationsFilter(filter)
    }

    const handleOperationClick = (operation: Operation) => {
        setShowOperation(true);
        setSelectedOperation(operation);
    }

    const handleDownload = (uuid: string, downloadOptions: Array<MultimodalDownloadOption>) => {
        // Do something about "response Type" blob :((
        state.BiometricDataApi.downloadBiometricDataByUUID({uuid: uuid, downloadOptions: downloadOptions}, {responseType: "blob"}).then(res => {
            fileDownload(res.data as any, "operation.zip");
        }).catch(err => {
            enqueueSnackbar("Unable to download", {variant: "error"})
            console.error(err)
        })
    } 

    const handleDownloadBulk = (downloadOptions: Array<MultimodalDownloadOption>) => {
        if (selected) {
            let idList: number[] = []
            selected.forEach(id => idList.push(id.id))
            state.BiometricDataApi.downloadBiometricDataBulkByOperation({ids: idList, downloadOptions: downloadOptions}, {responseType: "blob"}).then(res => {
                fileDownload(res.data as any, "operations.zip");
            }).catch(err => {
                enqueueSnackbar("Unable to download", {variant: "error"})
                console.error(err)
            })
        }
    }

    const handleOperationDelete = () => {
        if (selectedOperation && selectedOperation.uuid) {
            state.OperationsApi.deleteOperation({uuid: selectedOperation.uuid}).then(res => {
                enqueueSnackbar("Operation deleted successfully", { variant: 'success' });
                setSelectedOperation(undefined);
                setShowOperation(false)
                setOperationsFilter(operationsFilter)
                fetchOperation()
            }).catch(err => {
                enqueueSnackbar(err.message, { variant: 'error' });
            })
        }
    }

    const handleBulkDelete = () => {
        let uuidList: string[] = []
        selected.forEach(id => uuidList.push(id.uuid))
        state.OperationsApi.deleteOperations({uuids: uuidList}).then(res => {
            if (res.data === 0) {
                enqueueSnackbar("Zero Operations deleted, Operations with operation type \"Create template\" can't be deleted ", { variant: 'warning' });
            } else {
                enqueueSnackbar("Selected " + res.data + " operations successfully deleted", { variant: 'info' });
                fetchOperation()
            }
            setSelected([]);
        }).catch(err => {
            enqueueSnackbar(err.message, {variant: "error"})
        })
    }

    const getHeader = () => {
        return (
            selected.length > 0?
            <EnhancedTableToolbar 
                confirmationButton={
                    <ConfirmationButton
                        icon={<DeleteIcon />}
                        buttonTitle="Delete"
                        title="Confirmation"
                        text="Are you sure you want to delete selected subject(s)? All subject information will be removed and this action cannot be undone."
                        cancelText="Cancel"
                        confirmText="Delete"
                        confirm={handleBulkDelete}
                        confirmColor='error'
                        style={{paddingLeft: "1rem"}}
                    />
                }    
                selected={selected.length} 
                onDownloadClick={handleDownloadBulk}
            />
            : 
            <OperationFilter
                filter={operationsFilter}
                onFilterApply={handleFilterChange}
            />
        )
    }

    return (
        <>
            <Container maxWidth="xl" sx={{ display: "flex", flexDirection: "column" }}>
                {getHeader()}
                <DataTable
                    data={operations}
                    filter={operationsFilter}
                    onFilterChange={handleFilterChange}
                    onRowClick={handleOperationClick}
                    headCells={getHeadCells()}
                    title={'Subjects'}
                    dateName={'date'}
                    setSelected={setSelected}
                    selected={selected}
                    type={getDataTableType()}
                />
                <DatatableFooter
                    page={page}
                    handlePageChange={handlePageChange}
                    count={operationsCount}
                    perPage={perPage}
                    setPerPage={setPerPage}
                    label='Operations'
                />
            </Container>
            <OverviewDialog
                singleOperation={selectedOperation}
                open={showOperation}
                onClose={() => setShowOperation(false)}
                onImageDelete={handleOperationDelete} 
                onImageDownload={handleDownload}
                isDeletable
                isOperation
            />
        </>
    )
}

export default Operations
